import { Entry } from 'contensis-management-api';
import path from 'path';
import { EntryBuilder } from './EntryBuilder';
import mapErrorToObject from './mapAppErrorToObject';
import { DownloadStatus, MigrateStatus } from '~/models';
import {
  assetsFolderRoot,
  checkDir,
  checkFile,
  copyFile,
} from '~/providers/FileProvider';
import ContensisRepository from '~/repositories/ContensisRepository';
import { url } from '~/util/cms-url';
import { compareFields, getCmsFilePath } from '~/util/EntryHelper';
import { Logger } from '~/util/logger';

/**
 * Build a complete entry specifically for the target repository, providing
 * context of source and target repositories we can check each field in the
 * given source entry for any dependencies and set a two-pass loading status
 * if any dependencies do not exist
 *
 * Output the entry as JSON for loading into the Management API using the `toJSON` method
 */
export class AssetEntryBuilder extends EntryBuilder {
  assetLocalPath: string;
  assetUri: string;
  downloadUri: string;
  localAssetsFolder: string;

  /** The CMS file name and path where the asset will be uploaded to */
  get filePath() {
    return getCmsFilePath(this.toJSON());
  }

  /** Download the asset from the CMS Image API if a source repo is available - CHALKY-1666 */
  get imageApiUri() {
    return this.isImage && this.source.repo?.RootUrl()
      ? `${this.source.repo.RootUrl()}/api/image/${this.originalId}`
      : undefined;
  }

  /** Assert if the entry we are building is an image */
  get isImage() {
    return this.sourceEntry.sys.contentTypeId === 'image';
  }

  constructor(
    contensis: ContensisRepository,
    source: ContensisRepository,
    sourceEntry: Entry
  ) {
    super(contensis, source, sourceEntry);

    this.localAssetsFolder = path.join(
      assetsFolderRoot,
      `${source.alias}-${source.projectId}`
    );
    this.assetLocalPath = path.join(
      this.localAssetsFolder,
      getCmsFilePath(sourceEntry) || sourceEntry.sys.uri || ''
    );
    this.assetUri = sourceEntry.sys.uri || getCmsFilePath(sourceEntry) || '';
    this.downloadUri =
      this.assetUri.startsWith('http') || !this.assetUri.startsWith('/')
        ? this.assetUri
        : `${
            source.context.payload.source?.assetHostname ||
            source.context.payload.target?.assetHostname ||
            url(source.alias, source.projectId).previewWeb
          }${this.assetUri}`;

    // Find existing asset in target
    // We may have set existing from outside
    if (!this.existing) {
      // Migrating default assets with models keeps entries in context
      // vs. classic entries migration will populate entries.raw
      const entries = contensis.context.entries.length
        ? contensis.context.entries
        : contensis.entries.raw;

      const existingById = entries?.find(
        (e) => e.sys.id === contensis.transformGuid(sourceEntry.sys.id || '')
      );

      const existingByFilePath = getCmsFilePath(sourceEntry)
        ? entries.find(
            (e) =>
              getCmsFilePath(e) &&
              getCmsFilePath(e) === getCmsFilePath(sourceEntry)
          )
        : undefined;

      if (existingByFilePath) {
        // Track asset ids found by file path so when transforming
        // dependency ids for target entries later we can lookup this
        // list and instead map the sys.id for the existing asset found
        // by filename and path
        contensis.existingAssetIds.push([
          this.id || '',
          existingByFilePath.sys.id || '',
          existingByFilePath.entryTitle,
        ]);
      }

      this.existing = existingById || existingByFilePath;
    }

    if (this.id !== this.existing?.sys.id && this.existing?.sys.id) {
      Logger.info(
        `[${this.contentTypeId}|${this.id}] '${this.existing.entryTitle}' Found asset '${this.existing.sys.id}' by folder path`
      );
      this.id = this.existing.sys.id;
    }
  }

  /**
   * Build and return a copy of the asset entry as JSON
   */
  toJSON = () => {
    const { contensis, sourceEntry } = this;

    // Map entry fields
    const entry = {
      entryTitle: sourceEntry.entryTitle,
      title: sourceEntry.entryTitle,
      entryDescription: sourceEntry.entryDescription,
      sys: sourceEntry.sys,
    } as Entry;

    if (this.contentTypeId?.includes('image')) {
      entry.altText = sourceEntry.altText;
      entry.caption = sourceEntry.caption;
    }
    // Create a sys object based on the source entry
    entry.sys = this.buildSysObject();

    // Add sys.metadata fingerprint
    this.mapEntryMetadata(entry);

    // Update the sys.id (if transformGuids is true)
    entry.sys.id = this.id;

    // Transform sys.properties.fileId
    if ((entry.sys as any).properties.fileId)
      (entry.sys as any).properties.fileId = contensis.transformGuid(
        (entry.sys as any).properties.fileId
      );

    return entry;
  };

  /**
   * Build a copy of the source entry for the current repository
   * and check against any existing entry to set a migrate status
   */
  build = () => {
    try {
      // Build the entry and set the migrate status
      // this.validate();

      // We won't bother building EntryFields for asset entries
      // as they are simple and do not contain other dependents

      this.status = this.checkStatus();
    } catch (ex: any) {
      this.error = mapErrorToObject(
        ex ||
          new Error(`Error building asset entry: ${this.sourceEntry.sys.id}`)
      );
    }
  };

  /**
   * Check the asset entry for update status
   */
  checkStatus = (): MigrateStatus => {
    const { error, existing, sourceEntry } = this;

    if (error) return 'error';

    const entry = this.toJSON();
    if (existing) {
      // Has the entry changed from the existing?

      const { changed, diff } = compareFields(
        this.nullFields(entry),
        this.nullFields(existing),
        this.source,
        this.contensis
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        if (this.shouldPublish(sourceEntry, existing)) return 'publish';
        return 'no change';
      }
    }

    const assetUri = entry.sys.uri || getCmsFilePath(entry);
    if (!assetUri) {
      this.error = mapErrorToObject(
        new Error(
          `[${entry.sys.contentTypeId}|${entry.sys.id}] '${entry.entryTitle}' does not have a sys.uri or sys.properties.filePath to create a uri to download from in '${this.source.project.id}'`
        )
      );
      return 'error';
    }

    return 'create';
  };

  /**
   * Download an asset from a given url to a given localPath
   */
  downloadAssetContent = async (
    url = this.downloadUri,
    localPath = path.dirname(this.assetLocalPath)
  ): Promise<[MappedError | undefined, DownloadStatus]> => {
    let error: MappedError | undefined;
    let downloaded = false;
    const filename = path.basename(this.assetLocalPath);
    const localFilePath = path.join(localPath, filename);
    try {
      const { contensis, source } = this;
      // Source repo can be undefined for entries supplied in the payload
      const repo = source.repo || contensis.repo;
      const download = repo.DownloadContentAsset;
      const hasCache = repo.cache.fileCache;

      // Need to check if image already exists
      // Bypass the check if we are not using a fileCache (i.e. during tests)
      if (hasCache && checkFile(localFilePath)) {
        // TODO: check for 0 byte file and delete it
        return [undefined, 'cached'];
      } else {
        // Need to check if folder already exists and create it if not
        checkDir(localFilePath);

        if (url.startsWith('http')) {
          const [err, stream] = await download(
            localFilePath,
            this.imageApiUri || encodeURI(url)
          );

          if (err) {
            throw err; // let catch handle this
          } else if (stream) {
            downloaded = await new Promise<boolean>((resolve, reject) => {
              stream
                .on('open', () => {
                  Logger.info(`[downloading] ${localFilePath}.`);
                })
                .on('finish', () => {
                  Logger.success(`[download] ${localFilePath}.`);
                  resolve(true);
                })
                .on('error', (err) => {
                  Logger.error(`[download] ${localFilePath}.`);
                  reject(err);
                });
            });
          }
        } else if (checkFile(url)) {
          // local file path
          copyFile(url, localFilePath);
        } else {
          throw new Error(`Not a HTTP URL or local file path`);
        }
      }
    } catch (ex: any) {
      error = mapErrorToObject(ex);
      Logger.error(
        `Error downloading asset '${filename}' to ${localPath} - ${error?.message || error?.status}`
      );
      return [error, 'error'];
    }
    return [error, downloaded ? 'downloaded' : 'error'];
  };
}
